import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("success");
        setMessage("Your email is verified. You&apos;re all set.");
      } catch (e) {
        setState("error");
        setMessage(formatApiError(e.response?.data?.detail) || "This link is invalid or has expired.");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="ambient-bg" />
      <div className="w-full max-w-md p-8 animate-in text-center">
        <div className="mb-6 flex justify-center">
          <BrandLogo variant="icon" iconSize={44} as="div" testid="verify-brand" />
        </div>
        {state === "verifying" && (
          <div data-testid="verify-loading" className="flex flex-col items-center gap-3 text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
            <span className="text-sm">Verifying your email…</span>
          </div>
        )}
        {state === "success" && (
          <div data-testid="verify-success" className="space-y-4">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-emerald-500/15 border border-emerald-500/25">
              <Check className="h-6 w-6 text-emerald-400" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-semibold text-zinc-50">Email verified</h1>
            <p className="text-sm text-zinc-400">
              You can now use every feature of Intuiterview without interruption.
            </p>
            <Link
              to="/app/dashboard"
              data-testid="verify-continue-link"
              className="inline-flex items-center gap-2 bg-amber-500 text-amber-950 hover:bg-amber-400 font-semibold rounded-lg px-5 py-2.5"
            >
              Continue to Dashboard
            </Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-error" className="space-y-4">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-rose-500/15 border border-rose-500/25">
              <AlertCircle className="h-6 w-6 text-rose-400" />
            </div>
            <h1 className="text-2xl font-semibold text-zinc-50">Verification failed</h1>
            <p className="text-sm text-zinc-400">{message}</p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 hover:bg-zinc-900 font-medium rounded-lg px-5 py-2.5"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
